import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// ActionCreators

import { changePage } from '@actions/ssap/ssapActionCreators';
import { householdMemberTools } from '@utils/tools/householdMemberTools';

// Styles

import { darculaHyperButtonsStyles, darculaTheme } from '../darculaTheme';

const {
    paginatorRootStyle,
    paginatorItemWrapperStyle,
    paginatorButtonBase,
    paginatorTooltipPanelStyle,
    paginatorTooltipTitleStyle,
    paginatorTooltipComponentStyle,
    paginatorTooltipHhmBlockStyle,
    paginatorButtonSelectedBoxShadow,
    paginatorButtonHrStyle,
} = darculaHyperButtonsStyles;

const TOOLTIP_VIEWPORT_PAD_PX = 8;
const TOOLTIP_GAP_PX = 8;
/** Do not let the panel grow taller than this much of the viewport on either side of the anchor. */
const MIN_TOOLTIP_VIEWPORT_MAX_PX = 120;

const HyperButtons = ({ adjustedFlow, householdMembers, page }) => {

    const dispatch = useDispatch();

    const handleClick = e => {
        const {
            name
        } = e.currentTarget;

        dispatch(changePage(+name));
    };

    const getReasons = reasons => {
        return reasons.map((reason, i) => (
            <div key={`${reason}_${i}`} style={{ color: darculaTheme.fg }}>
                {`${reason};`}
            </div>
        ));
    };

    const resetTooltipPanelLayout = panel => {
        panel.style.display = 'none';
        panel.style.transform = 'translateX(-50%)';
        panel.style.maxHeight = '';
        panel.style.top = '';
        panel.style.bottom = '';
        panel.style.marginTop = '';
        panel.style.marginBottom = '';
    };

    const handleMouseEnter = e => {
        const wrap = e.currentTarget;
        const panel = wrap.querySelector('.paginator-btn-tooltip');
        if (!panel) {
            return;
        }
        panel.style.display = 'block';
        panel.style.transform = 'translateX(-50%)';
        panel.style.maxHeight = 'none';

        requestAnimationFrame(() => {
            const wrapRect = wrap.getBoundingClientRect();
            const spaceAbove = Math.max(
                MIN_TOOLTIP_VIEWPORT_MAX_PX,
                wrapRect.top - TOOLTIP_VIEWPORT_PAD_PX - TOOLTIP_GAP_PX
            );
            const spaceBelow = Math.max(
                MIN_TOOLTIP_VIEWPORT_MAX_PX,
                window.innerHeight - wrapRect.bottom - TOOLTIP_VIEWPORT_PAD_PX - TOOLTIP_GAP_PX
            );
            const naturalH = panel.scrollHeight;

            const fitsAbove = naturalH <= spaceAbove;
            const fitsBelow = naturalH <= spaceBelow;
            let placeBelow = false;
            if (!fitsAbove && fitsBelow) {
                placeBelow = true;
            } else if (!fitsAbove && !fitsBelow) {
                placeBelow = spaceBelow >= spaceAbove;
            }

            if (placeBelow) {
                panel.style.bottom = 'auto';
                panel.style.top = '100%';
                panel.style.marginBottom = '0';
                panel.style.marginTop = `${TOOLTIP_GAP_PX}px`;
                panel.style.maxHeight = `${spaceBelow}px`;
            } else {
                panel.style.top = 'auto';
                panel.style.bottom = '100%';
                panel.style.marginTop = '0';
                panel.style.marginBottom = `${TOOLTIP_GAP_PX}px`;
                panel.style.maxHeight = `${spaceAbove}px`;
            }

            const rect = panel.getBoundingClientRect();
            let dx = 0;
            if (rect.left < TOOLTIP_VIEWPORT_PAD_PX) {
                dx = TOOLTIP_VIEWPORT_PAD_PX - rect.left;
            }
            if (rect.right > window.innerWidth - TOOLTIP_VIEWPORT_PAD_PX) {
                dx += window.innerWidth - TOOLTIP_VIEWPORT_PAD_PX - rect.right;
            }
            panel.style.transform = dx !== 0 ?
                `translateX(calc(-50% + ${dx}px))` :
                'translateX(-50%)';
        });
    };

    const handleMouseLeave = e => {
        const panel = e.currentTarget.querySelector('.paginator-btn-tooltip');
        if (!panel) {
            return;
        }
        resetTooltipPanelLayout(panel);
    };


    return (
        <div style={ paginatorRootStyle }>
            {
                adjustedFlow.map((currentPage, i) => {
                    const isSelectedPage = page === i;
                    const isFlowActive = currentPage.isActive;

                    const backgroundColor = isFlowActive ?
                        darculaTheme.buttonPressed :
                        (isSelectedPage ? darculaTheme.bgInput : darculaTheme.bg);

                    const boxShadow = isSelectedPage ? paginatorButtonSelectedBoxShadow : 'none';

                    return (
                        <div
                            key={ i }
                            style={ paginatorItemWrapperStyle }
                            onMouseEnter={ handleMouseEnter }
                            onMouseLeave={ handleMouseLeave }
                        >
                            <button
                                name={ i }
                                type="button"
                                onClick={ handleClick }
                                style={{
                                    ...paginatorButtonBase,
                                    zIndex: isSelectedPage ? 1 : 0,
                                    minWidth: String(i).length < 3 ? '2.5rem' : '2.85rem',
                                    fontWeight: isSelectedPage ? 600 : 500,
                                    backgroundColor,
                                    borderColor: isSelectedPage ? darculaTheme.lightPurple : darculaTheme.border,
                                    boxShadow,
                                }}
                            >
                                <span>
                                    { i }
                                </span>
                                {
                                    currentPage.hhm !== -1 &&

                                    <Fragment>
                                        <hr style={ paginatorButtonHrStyle }/>
                                        { currentPage.hhm }
                                    </Fragment>
                                }
                            </button>
                            <div
                                className="paginator-btn-tooltip"
                                style={ paginatorTooltipPanelStyle }
                            >
                                <div style={ paginatorTooltipTitleStyle }>
                                    <FormattedMessage id={currentPage.page.navBarText}/>
                                </div>
                                <div style={ paginatorTooltipComponentStyle }>
                                    { currentPage.componentName }
                                </div>
                                <div>
                                    {
                                        currentPage.reasons.length !== 0 ?
                                            getReasons(currentPage.reasons) :
                                            null
                                    }
                                </div>
                                <div>
                                    {
                                        currentPage.hhm !== -1 && currentPage.hhm < householdMembers.length ?
                                            <span style={ paginatorTooltipHhmBlockStyle }>
                                                <span style={{ color: darculaTheme.keywordOrange }}>Page for: </span>
                                                <span style={{ color: darculaTheme.fg }}>
                                                    {
                                                        householdMemberTools
                                                            .extractFromHouseholdMembers(householdMembers)
                                                            .getFullNameByHhmIndexInArray(currentPage.hhm)
                                                    }
                                                </span>
                                                <br />
                                                <span style={{ color: darculaTheme.lightPurple }}>Hhm&apos;s GUID: </span>
                                                <span style={{ color: darculaTheme.fg }}>
                                                    {
                                                        householdMemberTools
                                                            .extractFromHouseholdMembers(householdMembers)
                                                            .getGuidByIndexInArray(currentPage.hhm)
                                                    }
                                                </span>
                                            </span> : null
                                    }
                                </div>
                            </div>
                        </div>
                    );
                })
            }



        </div>
    );
};

export default HyperButtons;
